import { Component, computed, effect, ElementRef, inject, QueryList, signal, ViewChild, ViewChildren } from '@angular/core';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';
import { SkinningService } from '@core/skin';
import { PlayerService } from 'app/features/player/data-access/player.service';
import { Loader } from 'app/shared/components/loader/loader.component';
import { PlayerControlsSeriesEpisodeComponent } from './components/player-controls-series-episode/player-controls-series-episode';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { TranslocoPipe } from '@jsverse/transloco';
import { IEpisodeDetailsDto } from '@features/assets/data-access/series-api.dto';
import { assetDurationSeconds } from '@shared/utils/duration.helper';
import { PlaybackProgressStore } from 'app/features/player/data-access/playback-progress.store';

@Component({
  selector: 'player-controls-series-episodes',
  imports: [
    CommonModule,
    PlayerControlsSeriesEpisodeComponent,
    RoundedIconButton,
    Loader,
    TranslocoPipe,
    SelectModule,
    FormsModule
  ],
  templateUrl: './player-controls-series-episodes.html',
  styleUrl: './player-controls-series-episodes.scss'
})
export class PlayerControlsSeriesEpisodesComponent {
  public skinning = inject(SkinningService);
  private playerService = inject(PlayerService);
  private playbackProgress = inject(PlaybackProgressStore);
  private progressPercentage = this.playerService.getProgressPercentage();

  public seriesEpisodeWatchData = this.playerService.getSeriesEpisodeWatchData();
  public item = this.playerService.getItem();
  public loading = signal(true);

  // Bumped when an episode's availability flips, to re-pick which episode shows the auto badge (36029).
  private availabilityTick = signal(0);

  /** Only the latest available episode shows the automatic "new episode" badge (tie → highest episodeNumber). */
  public latestAvailableEpisodeReferenceId = computed<string | null>(() => {
    this.availabilityTick();
    const now = Date.now();
    let latestReferenceId: string | null = null;
    let latestTime = -Infinity;
    let latestEpisodeNumber = -Infinity;
    for (const episode of this.seriesEpisodeWatchData()?.seasonEpisodes?.episodes ?? []) {
      if (!episode.availabilityStartTime) continue;
      const startTime = new Date(episode.availabilityStartTime).getTime();
      if (startTime > now) continue;
      const episodeNumber = (episode as { episodeNumber?: number }).episodeNumber ?? -Infinity;
      if (startTime > latestTime || (startTime === latestTime && episodeNumber > latestEpisodeNumber)) {
        latestTime = startTime;
        latestEpisodeNumber = episodeNumber;
        latestReferenceId = episode.referenceId;
      }
    }
    return latestReferenceId;
  });

  onEpisodeAvailabilityChanged(): void {
    this.availabilityTick.update(v => v + 1);
  }

  @ViewChildren('episodeItem', { read: ElementRef }) episodeRefs!: QueryList<ElementRef>;
  @ViewChild('episodesContainer') episodesContainer!: ElementRef;

  constructor() {
    effect(() => {
      const seriesEpisodeWatchData = this.seriesEpisodeWatchData();
      if (!seriesEpisodeWatchData) return;
      this.loading.set(false);
    });
  }

  public scrollToActive() {
    if (!this.episodeRefs?.length || !this.episodesContainer) return;

    const activeIndex = this.seriesEpisodeWatchData()?.seasonEpisodes?.episodes
      .findIndex(ep => ep.referenceId === this.item()?.referenceId);

    if (activeIndex !== undefined && activeIndex !== -1) {
      const activeRef = this.episodeRefs.toArray()[activeIndex];
      if (activeRef) {
        const container = this.episodesContainer.nativeElement;
        const element = activeRef.nativeElement;

        // Calculate scroll position to center the element
        const top = element.offsetTop - (container.offsetHeight / 2) + (element.offsetHeight / 2);

        container.scrollTo({
          top: top,
          behavior: 'smooth'
        });
      }
    }
  }

  onSeasonSelect(seasonReferenceId: string) {
    this.loading.set(true);
    this.playerService.changeSeasonEpisodesById(seasonReferenceId);
  }

  /**
   * `watchData` is fetched once, when the episode starts, so on its own it would show the playing
   * episode at its start position and every episode played earlier in this player session at
   * whatever it had before. The playing episode follows the live position instead, and the others
   * take the bookmark the player wrote for them (`PlaybackProgressStore`) over the fetched one.
   */
  public getBookmarkPercentage(item: IEpisodeDetailsDto) {
    // The player resets this on every new source, so it never carries the previous episode's position.
    if (item.referenceId === this.item()?.referenceId) {
      return this.progressPercentage();
    }
    const durationInSeconds = assetDurationSeconds(item);
    const played = this.playbackProgress.bookmarks()[item.referenceId];
    if (played !== undefined) {
      // 0 is the player's "watched through" (bookmark removed), which the list shows as watched.
      if (played === 0) return 100;
      return durationInSeconds ? (played / durationInSeconds) * 100 : 0;
    }
    const bookmarkEpisode = this.seriesEpisodeWatchData().watchData.bookmarkEpisodes.find(bookmark => bookmark.episodeReferenceId === item.referenceId);
    const watchedEpisode = this.seriesEpisodeWatchData().watchData.watchedEpisodes.find(watched => watched === item.referenceId);
    if (bookmarkEpisode) {
      return (bookmarkEpisode.bookmark / durationInSeconds) * 100;
    }
    else if (watchedEpisode) {
      return 100;
    }
    else return 0;
  }

}

